import { Injectable } from '@angular/core';
import { BrowserStorageService } from '@grid-ui/common';
import { GLOBAL_ROUTING_ACTION_CREATORS } from '@grid-ui/global-routing';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AnnouncementsDialogComponent } from '../../feature/components/announcements-dialog/announcements-dialog.component';
import { AnnouncementHttpService } from '../api-service/announcements-http.service';
import { GLOBAL_MODULE_ANNOUNCEMENT_ACTION_CREATORS } from './global-module-announcement.actions';
import { AnnouncementType } from './global-module-announcement.reducer';
import { globalModuleAnnouncementSelectors } from './global-module-announcement.selectors';

const defaultModalOptions: NgbModalOptions = {
  centered: true,
  backdrop: 'static',
  keyboard: false,
  size: 'lg',
  scrollable: false,
  modalDialogClass: 'announcement-detail-modal',
};

const V4_MODULE_NAME_MAP: Record<string, { key: string; name: string }> = {
  global: { key: 'global', name: 'Global' },
  country: { key: 'country-risk', name: 'Country Risk' },
  'commodity-risk': { key: 'commodity-risk', name: 'Commodity Risk' },
  'industry-risk': { key: 'industry', name: 'Industry Risk' },
};

@Injectable()
export class GlobalModuleAnnouncementEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly announcementHttpService: AnnouncementHttpService,
    private readonly browserStorageService: BrowserStorageService,
    private readonly ngBoostrapModalService: NgbModal,
    private readonly store: Store,
  ) {}

  protected readonly loadAnnouncements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GLOBAL_MODULE_ANNOUNCEMENT_ACTION_CREATORS.load),
      switchMap(() =>
        this.announcementHttpService.getUnifiedAnnouncements().pipe(
          map((response) => {
            const popups = response.results.filter((a) => a.pop_up_announcement);
            return GLOBAL_MODULE_ANNOUNCEMENT_ACTION_CREATORS.loadSucceeded({
              payload: {
                globalAnnouncements: popups.map((popup) => ({
                  type: AnnouncementType.global,
                  id: popup.id.toString(),
                  title: popup.title,
                  text: popup.text,
                  isDismissed: true,
                  module: popup.global_in_app
                    ? { key: 'global', name: 'Global' }
                    : (V4_MODULE_NAME_MAP[popup.module] ?? { key: 'global', name: 'Global' }),
                })),
                moduleAnnouncements: [],
                dismissedItems: (this.browserStorageService.getLocal<any[] | null>('dismissedItems') ?? []).map((item) => item.toString()),
              },
            });
          }),
          catchError((error) => of(GLOBAL_MODULE_ANNOUNCEMENT_ACTION_CREATORS.loadFailed({ payload: { error: new Error(error) } }))),
        ),
      ),
    ),
  );

  protected readonly loadAnnouncementsSucceeded$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(GLOBAL_MODULE_ANNOUNCEMENT_ACTION_CREATORS.loadSucceeded)),
        this.actions$.pipe(ofType(GLOBAL_ROUTING_ACTION_CREATORS.routeChangeSuccess)),
      ]).pipe(
        withLatestFrom(this.store.pipe(select(globalModuleAnnouncementSelectors.selectNotDismissedAnnouncements))),
        tap(([, announcements]) => {
          const sessionDismissedAnnouncements: string[] | null = this.browserStorageService.getSession('dismissedSessionItems');
          if (sessionDismissedAnnouncements !== null && sessionDismissedAnnouncements.length > 0) {
            announcements = announcements.filter((ann) => !sessionDismissedAnnouncements.includes(ann.id));
          }

          if (announcements.length > 0) {
            void this.openGlobalAnnouncementsModal();
          }
        }),
      ),
    { dispatch: false },
  );

  private openGlobalAnnouncementsModal() {
    const modalRef = this.ngBoostrapModalService.open(AnnouncementsDialogComponent, defaultModalOptions);
    return modalRef.result;
  }
}
